import { useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import { generatePath, useNavigate } from "react-router-dom";

import Sidebar from "~/components/Sidebar";
import { INVOICE_DETAILS_PATH } from "~/constants/appRoutes.ts";
import {
	ALL_PROCESSED_INVOICES_QUERY_KEY,
	processedInvoiceQueries,
} from "~/modules/billing/api/processedInvoice/processedInvoiceQueries.ts";
import type { Invoice } from "~/modules/billing/api/processedInvoice/processedInvoiceTypes.ts";
import CreateReversalInvoiceForm
	from "~/modules/billing/components/InvoiceDetailsView/components/CreateReversalInvoiceSidebar/CreateReversalInvoiceForm.tsx";

type Props = {
	isOpen: boolean;
	onClose: () => void;
	originalInvoiceData: Invoice;
};

const CreateReversalInvoiceSidebar = ({
	isOpen,
	onClose,
	originalInvoiceData,
}: Props) => {
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const handleSuccess = useCallback(async (reversalInvoiceId: string) => {
		queryClient.invalidateQueries({ queryKey: ALL_PROCESSED_INVOICES_QUERY_KEY });
		queryClient.invalidateQueries({ queryKey: processedInvoiceQueries.details(originalInvoiceData.id).queryKey });
		if (originalInvoiceData.projectId) {
			queryClient.invalidateQueries({ queryKey: processedInvoiceQueries.byProject(originalInvoiceData.projectId).queryKey });
		}

		navigate(generatePath(INVOICE_DETAILS_PATH, { invoiceId: reversalInvoiceId }));
		onClose();
	}, [navigate, onClose, originalInvoiceData.id, originalInvoiceData.projectId, queryClient]);

	return (
		<Sidebar closeOnOutsideClick={false}
				 open={isOpen}
				 setOpen={() => onClose()}>
			{isOpen && <CreateReversalInvoiceForm onCancel={onClose}
												  onSuccess={handleSuccess}
												  originalInvoiceData={originalInvoiceData} />}
		</Sidebar>
	);
};

export default CreateReversalInvoiceSidebar;
